/**
 * Update one agent: what changed since its version, the exact commands (or the one-time move for agents from
 * before 1.7.0), then a live check until the agent reports the new version.
 */

import { CheckCircle2, Loader2 } from 'lucide-react';
import Modal from '@/components/common/Modal';
import { useAgents } from '@/hooks/useAgents';
import type { Agent, AgentRelease } from '@/types/agent';
import { CodeBlock, DownloadPackageButton, composeFile, migrationCommands, startCommands, upgradeCommands } from '@/components/agents/agentSetup';
import { IMPORTANCE_BADGES, compareVersions, updateFor } from '@/components/agents/agentUpdates';

export default function AgentUpdatePanel({ agent, releases, platformUrl, onClose }: {
  agent: Agent;
  releases: AgentRelease[];
  platformUrl: string;
  onClose: () => void;
}) {
  const update = updateFor(agent.version, releases);
  const { data: agents = [] } = useAgents({ refetchInterval: 5000 });
  const live = agents.find((a) => a.id === agent.id);
  if (!update || !agent.version) return null;
  const done = live?.version === update.latest;
  const renamed = compareVersions(agent.version, '1.7.0') < 0;

  return (
    <Modal isOpen onClose={onClose} title={`Update ${agent.name}`} size="xl">
      <div className="space-y-4 text-sm">
        <p className="text-gray-700">
          Running {agent.version}. Latest {update.latest}.{' '}
          {update.importance !== 'routine' && <strong className={update.importance === 'security' ? 'text-red-700' : 'text-amber-700'}>{IMPORTANCE_BADGES[update.importance]}</strong>}
        </p>

        <section aria-label="What's new" className="rounded-md bg-gray-50 p-3">
          <h4 className="font-semibold text-gray-800">What's new</h4>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-700">
            {update.releases.map((release) => (
              <li key={release.version}><strong>{release.version}</strong> ({release.released_at}): {release.notes.join(' ')}</li>
            ))}
          </ul>
        </section>

        <section aria-label="Update steps" className="space-y-2">
          <DownloadPackageButton agentVersion={update.latest} />
          {renamed ? (
            <>
              <p className="text-gray-600">This agent is from before 1.7.0, when it was called <code>velosynt-agent</code> and installed in a folder per version. Move it once: stop the old one, then install the new one next to it.</p>
              <CodeBlock>{migrationCommands(agent.version, update.latest)}</CodeBlock>
              <p className="text-gray-600">Create <code>docker-compose.yml</code> in the <code>vantage-agent</code> folder, reusing the API key from the old one (the agent keeps its registration), then start it. You can delete the old folder afterwards.</p>
              <CodeBlock>{composeFile(platformUrl, update.latest, '<the key from your old docker-compose.yml>', agent.connection_type)}</CodeBlock>
              <CodeBlock>{startCommands()}</CodeBlock>
            </>
          ) : (
            <>
              <p className="text-gray-600">On the agent's server. Your settings and the agent's saved state are kept:</p>
              <CodeBlock>{upgradeCommands(update.latest)}</CodeBlock>
            </>
          )}
        </section>

        {done ? (
          <p role="status" className="flex items-center gap-2 rounded-md bg-green-50 p-3 text-green-800"><CheckCircle2 className="h-5 w-5" />{agent.name} is now running {update.latest}.</p>
        ) : (
          <p role="status" className="flex items-center gap-2 rounded-md bg-gray-50 p-3 text-gray-700"><Loader2 className="h-5 w-5 animate-spin" />Waiting for {agent.name} to report {update.latest}… This updates within a minute of the agent restarting.</p>
        )}
        <div className="flex justify-end"><button type="button" className="btn-primary" onClick={onClose}>{done ? 'Done' : 'Close'}</button></div>
      </div>
    </Modal>
  );
}
